<template>
  <FormCard
    :title="$t('contactInput.title')"
    :subtitle="$t('contactInput.subtitle')"
    icon
    iconBgClass="bg-gradient-to-br from-blue-500 to-indigo-600"
  >
    <template #icon>
      <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    </template>

    <form @submit.prevent="handleSubmit" class="space-y-6" novalidate>
      <!-- Email Input -->
      <div v-if="authMethod === 'email'" class="form-group">
        <label class="form-label flex ">
          <div class="flex items-center gap-2">
            <span class="text-center font-bold block w-full">{{ $t('contactInput.emailLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        
        <div class="max-w-md mx-auto">
        <div class="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <div class="relative flex-1 sm:max-w-[160px]">
            <div class="form-input-display pl-10 pr-8 flex items-center justify-center bg-gray-50 h-full min-h-[42px] rounded-xl border border-gray-300">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span class="text-gray-600">{{ $t('contactInput.emailBadge') }}</span>
              </div>
            </div>
          </div>
          
          <div class="relative flex-2">
            <input
              ref="emailInput"
              type="email"
              :value="email"
              @input="$emit('update:email', $event.target.value)"
              @blur="$emit('validate-email')"
              :placeholder="$t('contactInput.emailPlaceholder')"
              class="form-input"
              :class="{ 'error': errors.email }"
              maxlength="254"
              autocomplete="email"
              required
            />
          </div>
        </div>
        </div>
        
        <div v-if="errors.email" class="form-error" role="alert">
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {{ errors.email }}
        </div>
        <p class="text-xs text-gray-500 mt-1 text-center">
          {{ $t('contactInput.emailHint') }}
        </p>
      </div>

      <!-- Phone Input with Country Flag Picker -->
      <div v-if="authMethod === 'phone'" class="form-group">
        <label class="form-label flex justify-center">
          <div class="flex items-center gap-2">
            <span class="text-center font-bold block w-full">{{ $t('contactInput.phoneLabel') }} <span class="text-red-500">*</span></span>
          </div>
        </label>
        
        <div class="max-w-md mx-auto">
        <div class="phone-wrapper" @click.stop>
          <button
            type="button"
            class="phone-trigger"
            @click="toggleCountryDropdown"
          >
            <img
              :src="getFlagUrl(currentCountry.code)"
              :alt="currentCountry.code"
              class="flag-img"
            />
            <span class="phone-dial-code">+{{ currentCountry.dial_code }}</span>
            <svg
              :class="['phone-chevron', showCountryDropdown && 'phone-chevron--open']"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          <input
            ref="phoneInput"
            type="tel"
            :value="whatsappLocal"
            @input="$emit('update:whatsappLocal', $event.target.value)"
            @blur="$emit('validate-phone')"
            :placeholder="$t('contactInput.phonePlaceholder')"
            class="phone-input"
            :class="{ 'phone-input--error': errors.whatsapp }"
            maxlength="15"
            autocomplete="tel"
            required
          />
          <!-- Country Dropdown -->
          <transition name="dropdown">
            <div v-if="showCountryDropdown" class="country-dropdown">
              <div class="country-search-wrap">
                <svg class="country-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <input
                  v-model="countrySearch"
                  type="text"
                  class="country-search-input"
                  :placeholder="$t('contactInput.searchCountry') || 'Search...'"
                  @click.stop
                  ref="countrySearchInput"
                />
              </div>
              <div class="country-list">
                <button
                  v-for="c in filteredCountries"
                  :key="c.code"
                  type="button"
                  :class="['country-item', c.code === currentCountry.code && 'country-item--active']"
                  @click="onSelectCountry(c)"
                >
                  <img :src="getFlagUrl(c.code)" :alt="c.code" class="flag-img" />
                  <span class="country-item-name">{{ c.name }}</span>
                  <span class="country-item-code">+{{ c.dial_code }}</span>
                </button>
                <div v-if="filteredCountries.length === 0" class="country-empty">
                  {{ $t('contactInput.noResults') || 'No results found' }}
                </div>
              </div>
            </div>
          </transition>
        </div>
        </div>
        
        <div v-if="errors.whatsapp" class="form-error" role="alert">
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {{ errors.whatsapp }}
        </div>
        <p class="text-xs text-gray-500 mt-1 text-center">
          {{ $t('contactInput.phoneHint') }}
        </p>
      </div>

      <!-- reCAPTCHA slot: filled by parent only when a second solve is needed.
           Renders inside the verification card, above the buttons. -->
      <div class="flex justify-center pt-2">
        <slot name="captcha"></slot>
      </div>

      <!-- Navigation Buttons -->
      <div class="flex justify-center gap-3 pt-4">
        <button 
          type="button" 
          @click="$emit('back')" 
          class="btn-secondary group"
        >
          <svg class="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
          {{ $t('contactInput.backBtn') }}
        </button>
        
        <button
          type="submit"
          class="btn-primary group"
          :disabled="loading || !canSubmit"
        >
          <svg v-if="loading" class="animate-spin h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <span>{{ loading ? $t('contactInput.sendingBtn') : $t('contactInput.sendBtn') }}</span>
          <svg v-if="!loading" class="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
      
      <p v-if="rateLimitMessage" class="text-yellow-600 text-sm text-center">{{ rateLimitMessage }}</p>
    </form>
  </FormCard>
</template>

<script>
import FormCard from '../ui/FormCard.vue';
import { countries, getFlag, findByCode } from '@/utils/countries';

export default {
  name: 'ContactInput',
  components: { FormCard },
  props: {
    authMethod: { type: String, required: true },
    email: { type: String, default: '' },
    whatsappLocal: { type: String, default: '' },
    selectedCountry: { type: String, default: 'CM' },
    // countries prop kept for backward compat but no longer needed from parent
    countries: { type: Array, default: () => [] },
    errors: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    canSubmit: { type: Boolean, default: false },
    rateLimitMessage: { type: String, default: '' }
  },
  emits: [
    'submit', 'back', 'update:email', 'update:whatsappLocal',
    'update:whatsappFull', 'validate-email', 'validate-phone', 'country-change'
  ],
  data() {
    return {
      allCountries: countries,
      currentCountry: findByCode(this.selectedCountry) || countries.find(c => c.code === 'CM') || countries[0],
      showCountryDropdown: false,
      countrySearch: ''
    };
  },
  computed: {
    filteredCountries() {
      if (!this.countrySearch.trim()) return this.allCountries;
      const q = this.countrySearch.toLowerCase().trim();
      return this.allCountries.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.dial_code.includes(q) ||
        c.code.toLowerCase().includes(q)
      );
    }
  },
  watch: {
    // Sync if parent changes selectedCountry prop
    selectedCountry(newCode) {
      const found = findByCode(newCode);
      if (found) this.currentCountry = found;
    }
  },
  methods: {
    getFlagUrl(code) {
      return getFlag(code, 40);
    },

    toggleCountryDropdown() {
      this.showCountryDropdown = !this.showCountryDropdown;
      if (this.showCountryDropdown) {
        this.countrySearch = '';
        this.$nextTick(() => {
          if (this.$refs.countrySearchInput) this.$refs.countrySearchInput.focus();
        });
      }
    },

    onSelectCountry(c) {
      this.currentCountry = c;
      this.showCountryDropdown = false;
      this.countrySearch = '';
      // Emit country code for backward compat with parent
      this.$emit('country-change', c.code);
    },

    handleOutsideClick(e) {
      if (this.showCountryDropdown && !e.target.closest('.phone-wrapper')) {
        this.showCountryDropdown = false;
      }
    },

    /**
     * Build full international phone number.
     * If the user already typed the country code, don't double-prefix it.
     */
    buildFullPhone(localNumber) {
      if (!localNumber || !localNumber.trim()) return '';
      const digits = localNumber.replace(/\D/g, '').replace(/^0+/, '');
      const dialCode = this.currentCountry.dial_code;
      if (digits.startsWith(dialCode)) {
        return '+' + digits;
      }
      return '+' + dialCode + digits;
    },

    handleSubmit() {
      // Emit full phone number with country code before submitting
      if (this.authMethod === 'phone' && this.whatsappLocal) {
        const fullPhone = this.buildFullPhone(this.whatsappLocal);
        this.$emit('update:whatsappFull', fullPhone);
      }
      this.$emit('submit');
    }
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  }
}
</script>

<style scoped>
.form-group {
  @apply w-full space-y-2;
}

.form-label {
  @apply block text-sm font-semibold text-gray-700;
}

.form-input {
  @apply w-full px-4 py-3 bg-white text-gray-900 rounded-xl border border-gray-300 
         focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300
         placeholder-gray-500 hover:border-gray-400 shadow-sm;
}

.form-input:focus {
  @apply bg-white shadow-lg shadow-blue-500/10;
}

.form-input.error {
  @apply border-red-500 focus:ring-red-500 focus:border-red-500;
}

.form-error {
  @apply text-red-600 text-sm flex items-center justify-center mt-1;
}

/* ── Phone / Country Picker ── */
.phone-wrapper {
  position: relative;
  display: flex;
  align-items: stretch;
  border: 1px solid #d1d5db;
  border-radius: 0.75rem;
  background: #fff;
  overflow: visible;
  transition: border-color 0.2s, box-shadow 0.2s;
  z-index: 20;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.phone-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.phone-wrapper:hover {
  border-color: #9ca3af;
}

.phone-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  border: none;
  border-right: 1px solid #e5e7eb;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.phone-trigger:hover { background: #f9fafb; }

.flag-img {
  width: 24px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.08);
  flex-shrink: 0;
}

.phone-dial-code {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

.phone-chevron {
  width: 14px;
  height: 14px;
  color: #9ca3af;
  transition: transform 0.2s;
}

.phone-chevron--open { transform: rotate(180deg); }

.phone-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 12px 14px;
  font-size: 16px;
  font-family: inherit;
  color: #111827;
  background: transparent;
  min-width: 0;
  border-radius: 0 0.75rem 0.75rem 0;
}

.phone-input::placeholder { color: #6b7280; }
.phone-input--error { background: #fef2f2; }

/* ── Country Dropdown ── */
.country-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 320px;
  width: max-content;
  max-width: 400px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08);
  z-index: 9999;
  overflow: hidden;
}

.country-search-wrap {
  position: relative;
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
}

.country-search-icon {
  position: absolute;
  left: 22px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: #9ca3af;
  pointer-events: none;
}

.country-search-input {
  width: 100%;
  padding: 8px 12px 8px 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #111827;
  outline: none;
  background: #f9fafb;
  transition: border-color 0.15s;
}

.country-search-input:focus { border-color: #3b82f6; background: #fff; }
.country-search-input::placeholder { color: #9ca3af; }

.country-list { max-height: 300px; overflow-y: auto; }
.country-list::-webkit-scrollbar { width: 5px; }
.country-list::-webkit-scrollbar-track { background: #f9fafb; }
.country-list::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }

.country-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: none;
  font-size: 0.85rem;
  color: #111827;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
}

.country-item:hover { background: #eff6ff; }
.country-item--active { background: #eef2ff; font-weight: 600; }
.country-item-name { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.country-item-code { color: #9ca3af; font-size: 0.8rem; font-variant-numeric: tabular-nums; flex-shrink: 0; }
.country-empty { padding: 20px 14px; text-align: center; color: #9ca3af; font-size: 0.85rem; }

/* Dropdown transition */
.dropdown-enter-active { transition: all 0.2s cubic-bezier(0.34,1.56,0.64,1); }
.dropdown-leave-active { transition: all 0.15s ease; }
.dropdown-enter-from { opacity: 0; transform: translateY(-8px) scale(0.97); }
.dropdown-leave-to { opacity: 0; transform: translateY(-4px); }

.btn-primary {
  @apply bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700
         text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg
         transition-all duration-300 flex items-center justify-center disabled:opacity-50
         disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98];
}

.btn-secondary {
  @apply bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-5 rounded-xl
         shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center
         border border-gray-300 transform hover:scale-[1.02] active:scale-[0.98];
}

@media (max-width: 640px) {
  .country-dropdown { min-width: 100%; max-width: 360px; }
}
</style>